import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, Clock, ArrowRight, TrendingUp, AlertTriangle, FileText } from 'lucide-react';

const ARTICLES = [
  {
    id: 'accident-prone-areas',
    title: 'Top Accident Prone Areas in Kalaburagi 2026',
    excerpt: 'A data-driven analysis of the most dangerous intersections and road stretches in Kalaburagi district. Know the hotspots and drive safe.',
    category: 'Safety',
    readTime: '5 min',
    date: 'Mar 10, 2026',
    icon: AlertTriangle,
    content: `
## Top Accident Prone Areas in Kalaburagi

Based on traffic data and citizen reports, these are the most accident-prone areas in Kalaburagi district:

### 1. Jagat Circle
- **Risk Level:** Very High
- **Common Issues:** Signal jumping, wrong-way driving, heavy congestion during peak hours
- **Accidents in 2025:** 47 reported incidents
- **Tip:** Always follow traffic signals. Police deployment has increased in this area.

### 2. Super Market Area
- **Risk Level:** High  
- **Common Issues:** Wrong parking, pedestrian crossings blocked, auto-rickshaw congestion
- **Accidents in 2025:** 38 reported incidents
- **Tip:** Use designated parking areas. Watch for pedestrians crossing unexpectedly.

### 3. Ring Road - Humnabad Stretch
- **Risk Level:** High
- **Common Issues:** Overspeeding, poor lighting at night, unmarked speed breakers
- **Accidents in 2025:** 35 reported incidents
- **Tip:** Maintain speed limit of 60 km/h. Use headlights at night.

### 4. University Road
- **Risk Level:** Medium-High
- **Common Issues:** Triple riding (students), no helmet violations, sudden U-turns
- **Accidents in 2025:** 28 reported incidents
- **Tip:** Always wear a helmet. Avoid U-turns at unmarked spots.

### 5. Station Road
- **Risk Level:** Medium
- **Common Issues:** Heavy vehicle traffic, narrow lanes, waterlogging during monsoon
- **Accidents in 2025:** 22 reported incidents
- **Tip:** Allow extra time during monsoon season. Watch for heavy vehicles.

### How to Stay Safe
1. Always wear a helmet and seatbelt
2. Follow speed limits strictly
3. Never use mobile phone while driving
4. Report unsafe road conditions using Kalaburagi Traffic
5. Avoid driving during heavy rain or fog

### Report Issues
See a dangerous spot? Report it through our platform to help traffic police take action.
    `
  },
  {
    id: 'traffic-rules-2026',
    title: 'New Traffic Rules 2026 - Karnataka',
    excerpt: 'Updated fine amounts and new rules for 2026 in Karnataka. From helmet violations to drunk driving, know the latest penalties.',
    category: 'Rules',
    readTime: '7 min',
    date: 'Mar 5, 2026',
    icon: FileText,
    content: `
## New Traffic Rules 2026 - Karnataka

The Karnataka government has updated traffic violation fines for 2026. Here's the complete list:

### Updated Fine List

| Violation | Fine Amount |
|-----------|------------|
| Driving without license | Rs. 5,000 |
| Driving without insurance | Rs. 2,000 |
| No helmet (rider) | Rs. 1,000 |
| No helmet (pillion) | Rs. 1,000 |
| No seatbelt | Rs. 1,000 |
| Using mobile while driving | Rs. 5,000 |
| Signal jumping | Rs. 1,000 - 5,000 |
| Wrong parking | Rs. 500 - 1,500 |
| Overspeeding | Rs. 1,000 - 2,000 |
| Triple riding | Rs. 1,000 |
| Drunk driving | Rs. 10,000 |
| Dangerous driving | Rs. 5,000 |
| No pollution certificate | Rs. 1,000 |
| Overloading | Rs. 2,000 - 5,000 |

### New Rules for 2026
1. **Mandatory high-vis stickers** on two-wheelers for night driving
2. **Digital challan system** - All challans now issued digitally
3. **Repeat offender penalty** - Double fine for second offense within 90 days
4. **School zone speed limit** - Maximum 25 km/h near schools
5. **EV lane marking** - Designated lanes for electric vehicles on Ring Road

### How to Pay Challans Online
1. Visit Karnataka Traffic Police portal
2. Enter vehicle number or challan ID
3. Pay via UPI, net banking, or card
4. Download receipt

### Know Your Rights
- Police must show ID before issuing challan
- You can ask for the challan to be written, not just verbal
- You have 60 days to contest a challan
- Body cameras are now mandatory for traffic police
    `
  },
  {
    id: 'fine-list-violations',
    title: 'Complete Fine List for Traffic Violations in Karnataka',
    excerpt: 'Comprehensive list of all traffic violation fines applicable in Karnataka state. Updated for 2026 with new penalty amounts.',
    category: 'Reference',
    readTime: '4 min',
    date: 'Feb 28, 2026',
    icon: TrendingUp,
    content: `
## Complete Fine List - Traffic Violations Karnataka 2026

### Minor Violations
- **No parking sign violation:** Rs. 500
- **Wrong parking:** Rs. 500 - 1,500
- **No PUC certificate:** Rs. 1,000
- **Not carrying documents:** Rs. 500
- **Defective number plate:** Rs. 500

### Moderate Violations  
- **No helmet:** Rs. 1,000
- **No seatbelt:** Rs. 1,000
- **Triple riding:** Rs. 1,000
- **Signal jumping:** Rs. 1,000 - 5,000
- **Wrong side driving:** Rs. 1,000 - 5,000
- **Overspeeding:** Rs. 1,000 - 2,000

### Severe Violations
- **Driving without license:** Rs. 5,000
- **Using mobile while driving:** Rs. 5,000
- **Dangerous/rash driving:** Rs. 5,000
- **Drunk driving:** Rs. 10,000
- **Racing on public road:** Rs. 5,000
- **Causing accident:** Rs. 5,000 - 25,000

### Vehicle-Related Fines
- **No insurance:** Rs. 2,000
- **Overloading (goods):** Rs. 2,000 - 5,000
- **Modified silencer:** Rs. 1,000
- **Tinted windows:** Rs. 500
- **No fitness certificate:** Rs. 2,000

### Tips to Avoid Fines
1. Keep all documents in your vehicle
2. Always wear helmet/seatbelt
3. Follow traffic signals strictly
4. Don't use phone while driving
5. Report unsafe drivers on Kalaburagi Traffic
    `
  }
];

export default function BlogPage() {
  const [selectedArticle, setSelectedArticle] = React.useState(null);

  if (selectedArticle) {
    const article = ARTICLES.find(a => a.id === selectedArticle);
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="blog-article-page">
        <Button variant="ghost" className="gap-2 mb-6" onClick={() => setSelectedArticle(null)} data-testid="back-to-blog">
          <ArrowRight className="w-4 h-4 rotate-180" /> Back to Articles
        </Button>
        <article>
          <Badge className="mb-3">{article.category}</Badge>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{article.title}</h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{article.readTime} read</span>
            <span>{article.date}</span>
          </div>
          <div className="prose prose-sm max-w-none dark:prose-invert">
            {article.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold mt-8 mb-4">{line.replace('## ', '')}</h2>;
              if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('### ', '')}</h3>;
              if (line.startsWith('| ')) {
                const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
                if (line.includes('---')) return null;
                return (
                  <div key={i} className="grid grid-cols-2 gap-2 py-2 border-b text-sm">
                    {cells.map((cell, j) => (
                      <span key={j} className={j === 0 ? 'font-medium' : 'text-right'}>{cell}</span>
                    ))}
                  </div>
                );
              }
              if (line.startsWith('- **')) {
                const parts = line.replace('- **', '').split('**');
                return <div key={i} className="flex items-start gap-2 py-1 text-sm"><span className="font-bold">{parts[0]}</span><span className="text-muted-foreground">{parts[1]}</span></div>;
              }
              if (line.match(/^\d+\. /)) return <div key={i} className="py-1 text-sm pl-4">{line}</div>;
              if (line.trim() === '') return <br key={i} />;
              return <p key={i} className="text-sm text-muted-foreground mb-2">{line}</p>;
            })}
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="blog-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Traffic Safety Blog</h1>
          <p className="text-sm text-muted-foreground">Latest updates, rules, and safety information</p>
        </div>
      </div>

      <div className="space-y-4">
        {ARTICLES.map((article, i) => (
          <Card
            key={article.id}
            className="hover:shadow-md transition-shadow cursor-pointer animate-fade-in-up"
            style={{ animationDelay: `${i * 0.1}s` }}
            onClick={() => setSelectedArticle(article.id)}
            data-testid={`blog-card-${article.id}`}
          >
            <CardContent className="p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <article.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs">{article.category}</Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />{article.readTime}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold mb-1 hover:text-primary transition-colors">{article.title}</h2>
                  <p className="text-sm text-muted-foreground line-clamp-2">{article.excerpt}</p>
                  <span className="text-xs text-muted-foreground mt-2 block">{article.date}</span>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
